// src/utils/trpc.ts
import { httpBatchLink, loggerLink, httpLink,
  splitLink, } from '@trpc/client';
import { createTRPCNext } from '@trpc/next';
import { MutationCache, QueryCache } from '@tanstack/react-query';
import { signOut } from 'next-auth/react';
import type { AppRouter } from '../server/trpc/router';
import superjson from 'superjson';
import {
  PASSWORD_EXPIRED_REDIRECT,
  isPasswordExpiredError,
} from './auth/passwordExpiredNotice';

const getBaseUrl = () => {
  if (typeof window !== 'undefined') return ''; // browser should use relative url
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`; // SSR should use vercel url
  return `http://localhost:${process.env.PORT ?? 3000}`; // dev SSR should use localhost
};

// Every authed call keeps returning PASSWORD_EXPIRED while the watermark stands, so
// whichever query or mutation happens to fail first can act on it. A global handler
// rather than a check inside any one component, since there is no guarantee which
// call fires first on a given screen.
let redirectingToPasswordExpired = false;

/**
 * Sends a user whose credentials were revoked to the reset-password flow.
 *
 * Matches on the PASSWORD_EXPIRED marker rather than the UNAUTHORIZED code, because
 * the admin/organization/owner guards all throw UNAUTHORIZED for permission failures
 * — keying on the code would sign out a valid user for touching an admin-only screen.
 *
 * signOut rather than a bare redirect: the server deliberately leaves the cookie in
 * place (clearing it there erased the marker before the client could read it), so
 * this is where the stale session actually gets cleaned up. It also does a full
 * navigation, which drops the query cache along with it.
 */
const redirectIfPasswordExpired = (error: unknown) => {
  if (typeof window === 'undefined') return;
  if (redirectingToPasswordExpired) return;
  if (!isPasswordExpiredError(error)) return;

  redirectingToPasswordExpired = true;
  void signOut({ callbackUrl: PASSWORD_EXPIRED_REDIRECT, redirect: true });
};

export const trpc = createTRPCNext<AppRouter>({
  transformer: superjson,
  config() {
    return {
      // Both caches, so a revoked session is caught whether the first failing call
      // is a query or a mutation.
      queryClientConfig: {
        queryCache: new QueryCache({ onError: redirectIfPasswordExpired }),
        mutationCache: new MutationCache({ onError: redirectIfPasswordExpired }),
      },
      links: [
        loggerLink({
          enabled: (opts) =>
            process.env.NODE_ENV === 'development' ||
            (opts.direction === 'down' && opts.result instanceof Error),
        }),
        // httpBatchLink({
        //   url: `${getBaseUrl()}/api/trpc`,
        // }),
        splitLink({
          condition(op) {
            // check for context property `skipBatch`
            return op.context.skipBatch === true;
          },
          // when condition is true, use normal request
          true: httpLink({
            url: `${getBaseUrl()}/api/trpc`,
            transformer: superjson,
          }),
          // when condition is false, use batching
          false: httpBatchLink({
            url: `${getBaseUrl()}/api/trpc`,
            transformer: superjson,
          }),
        }),
      ],
    };
  },
  ssr: false,
});
